import React from "react";
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Typography} from "@mui/material";
import {HighlightOff} from "@mui/icons-material";
import EnhancedErrorDialog from "../../../components/EnhancedErrorDialog";
import {utilDeletePartnerSuspension} from "../../../utils/utils";
import { DialogHeader } from "../../../theme";

interface PartnerUnpauseDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    partnerIds: (number | string)[];
    onUnpause?: () => void;
}

export const PartnerUnpauseDialog = (props: PartnerUnpauseDialogProps) => {
    const {open, setOpen, partnerIds, onUnpause} = props;
    const [error, setError] = React.useState({open: false, errorHeader: "", errorMessage: ""});
    const [submitting, setSubmitting] = React.useState(false);

    const submit = async () => {
        if (!partnerIds?.length) return; // nothing to unpause — no-op
        setSubmitting(true);

        // utilDeletePartnerSuspension is async: it resolves to undefined on success
        // or { open, errorHeader, errorMessage } on failure (it never rejects). Await
        // the results, then inspect the resolved values — the previous code read
        // `.error` off the un-awaited Promise (a key that doesn't exist anyway), so
        // failures were never detected and were silently reported as success.
        const responses = await Promise.all(
            partnerIds.map(partnerId => utilDeletePartnerSuspension(partnerId))
        );
        const failures = responses.filter((response: any) => response && response.open);

        if (failures.length > 0) {
            const detail = Array.from(
                new Set(failures.map(f => f.errorMessage).filter(Boolean))
            ).join("\n");
            setError({
                open: true,
                errorHeader: failures[0].errorHeader || "Error deleting partner suspension!",
                errorMessage: detail || `${failures.length} of ${partnerIds.length} unpause requests failed.`,
            });
            setSubmitting(false);
            return;
        }

        await new Promise(r => setTimeout(r, 750));
        setOpen(false);
        if (onUnpause) onUnpause();
        setSubmitting(false);
    }

    return (
        <>
            <Dialog open={open} maxWidth={"sm"} fullWidth={true}>
                <DialogTitle style={{paddingBottom: "0px"}}>
                    <Grid container justifyContent={"space-between"} alignItems={"center"}>
                        <Grid item container style={{flex: 1, flexDirection: "column"}}>
                            <DialogHeader>
                                Unpause
                            </DialogHeader>
                        </Grid>

                        <IconButton onClick={() => setOpen(false)} size="large">
                            <HighlightOff style={{color: "red"}}/>
                        </IconButton>
                    </Grid>
                </DialogTitle>

                <DialogContent style={{paddingTop: "0", paddingBottom: "0"}}>
                    <Typography>
                        Are you sure you want to unpause {partnerIds.length > 1 ? "these" : "this"} {partnerIds.length > 1 ? "suppliers" : "supplier"}?
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button
                        style={{marginTop: "24px"}}
                        variant={"text"}
                        color={"primary"}
                        onClick={() => submit()}
                    >
                        Unpause
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={submitting} onClose={(event: any, reason: string) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            }}>
                <DialogTitle>
                    <DialogHeader>
                        Submitting Unpause Request
                    </DialogHeader>
                </DialogTitle>
                <DialogContent>
                    <Grid container justifyContent={"center"} alignItems={"center"} direction={"column"}>
                        <CircularProgress/>
                    </Grid>
                </DialogContent>
            </Dialog>
            <EnhancedErrorDialog open={error.open} onClose={() => setError({...error, open: false})}
                                 errorHeader={error.errorHeader} errorMessage={error.errorMessage}/>
        </>
    );
}
